import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import * as authActions from '../../features/auth/actions';
import * as sharedActions from '../../shared/actions';
import { ILegalEntitiesBusinessesResponse } from '../../shared/models/shared.model';
import { RouteURL, SessionStorageIds } from '../../shared/enums';
import { LegalEntitySelectionService } from './legal-entity-selection.service';

// Role names (from Authentication/validateToken) that work within their first legal entity, and the page each lands on.
// Checked in order; the first match wins.
const LEGAL_ENTITY_LANDING_ROLES: { pattern: RegExp; route: string }[] = [
  { pattern: /william\s*strike/i, route: RouteURL.WS_PAYROLL_UPLOAD }, // "WilliamStrikeView": payroll upload only
  { pattern: /advis[eo]r/i, route: RouteURL.LEGAL_ENTITY_DASHBOARD }, // "Adviser" / "Advisor"
  { pattern: /employer/i, route: RouteURL.LEGAL_ENTITY_DASHBOARD }
];

/**
 * Decides where a user lands after login: advisers and employers go straight to the dashboard of their first
 * legal entity, William Strike users to its payroll upload, and everyone else (or one with no legal entities)
 * to the Admin Dashboard.
 */
@Injectable({
  providedIn: 'root'
})
export class AdviserLandingService {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private legalEntitySelectionService: LegalEntitySelectionService
  ) {}

  /** Call before dispatching loadValidateToken so its result is not missed. */
  navigateAfterLogin(): void {
    this.actions$
      .pipe(
        ofType(authActions.loadValidateTokenSuccess, authActions.loadValidateTokenFailure),
        take(1),
        switchMap(action =>
          action.type === authActions.loadValidateTokenSuccess.type && AdviserLandingService.landsOnLegalEntity(action.response.roles || [])
            ? this.loadFirstLegalEntity()
            : of(null)
        )
      )
      .subscribe(legalEntity => {
        if (legalEntity) {
          this.legalEntitySelectionService.select(legalEntity);
          this.router.navigateByUrl(AdviserLandingService.landingRoute() ?? RouteURL.LEGAL_ENTITY_DASHBOARD);
        } else {
          this.router.navigateByUrl(RouteURL.ADMIN_DASHBOARD);
        }
      });
  }

  /** Selects the first legal entity and opens the role's landing page (e.g. from the toolbar logo); stays put if there is none. */
  navigateToFirstLegalEntity(): void {
    this.loadFirstLegalEntity().subscribe(legalEntity => {
      if (legalEntity) {
        this.legalEntitySelectionService.select(legalEntity);
        this.router.navigateByUrl(AdviserLandingService.landingRoute() ?? RouteURL.LEGAL_ENTITY_DASHBOARD);
      }
    });
  }

  /** True for adviser / employer / William Strike roles; with no argument, checks the roles stored at login. */
  static landsOnLegalEntity(roles?: string[] | null): boolean {
    return AdviserLandingService.landingRoute(roles) !== null;
  }

  /** Where picking a legal entity (search popup, Recent History) takes the user: their role's landing page, else the entity dashboard. */
  static legalEntityHomeRoute(): string {
    return AdviserLandingService.landingRoute() ?? RouteURL.LEGAL_ENTITY_DASHBOARD;
  }

  /** True for William Strike users, who only get Upload Payroll. */
  static isPayrollOnly(): boolean {
    return AdviserLandingService.landingRoute() === RouteURL.WS_PAYROLL_UPLOAD;
  }

  /** The page the user's role lands on inside their legal entity, or null for roles that use the Admin Dashboard. */
  static landingRoute(roles?: string[] | null): string | null {
    if (roles === undefined) {
      try {
        roles = JSON.parse(sessionStorage.getItem(SessionStorageIds.AUTH_ROLES) || '[]');
      } catch {
        roles = [];
      }
    }
    const roleNames = roles || [];
    const match = LEGAL_ENTITY_LANDING_ROLES.find(landing => roleNames.some(role => landing.pattern.test(role)));
    return match ? match.route : null;
  }

  private loadFirstLegalEntity(): Observable<ILegalEntitiesBusinessesResponse | null> {
    const firstLegalEntity$ = this.actions$.pipe(
      ofType(sharedActions.loadLegalEntitiesSuccess, sharedActions.loadLegalEntitiesFailure),
      take(1),
      map(action => {
        if (action.type !== sharedActions.loadLegalEntitiesSuccess.type) {
          return null;
        }
        const legalEntities = (action.response || []) as ILegalEntitiesBusinessesResponse[];
        if (legalEntities.length) {
          // Cache like the legal entity search popup does; the toolbar only shows the
          // Legal Entities / Recent History menu outside the global pages when this is set
          sessionStorage.setItem(SessionStorageIds.LEGAL_ENTITIES, JSON.stringify(legalEntities));
        }
        return legalEntities[0] ?? null;
      })
    );
    // Same list (and order) the legal entity search popup shows
    setTimeout(() => this.store.dispatch(sharedActions.loadLegalEntities({ param: '', noLoader: false })));
    return firstLegalEntity$;
  }
}
